import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, AlertTriangle, CheckCircle2, Settings2, Zap, Copy, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fetchDealPropertyDefs, fetchPortalArrConfig, savePortalArrConfig } from "@/lib/api";
import { cn } from "@/lib/utils";

// ── Searchable single-select property combobox ───────────────────────────
function PropertyCombobox({ value, onChange, properties, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const selected = properties.find((p) => p.name === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between border-[#dddddd] bg-white text-left font-normal hover:bg-[#f5f5f5]"
        >
          <span className="truncate">
            {selected ? (
              <span>
                <span className="font-medium text-[#323c44]">{selected.label}</span>
                <span className="ml-2 font-mono text-xs text-[#7d8b96]">{selected.name}</span>
              </span>
            ) : (
              <span className="text-[#7d8b96]">{placeholder || "Select a property…"}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-[#7d8b96]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[480px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search properties…" />
          <CommandList>
            <CommandEmpty>No properties found.</CommandEmpty>
            <CommandGroup>
              {properties.map((prop) => (
                <CommandItem
                  key={prop.name}
                  value={`${prop.label} ${prop.name}`}
                  onSelect={() => {
                    onChange(prop.name);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === prop.name ? "opacity-100" : "opacity-0")} />
                  <span className="flex-1 font-medium text-[#323c44]">{prop.label}</span>
                  <span className="ml-3 font-mono text-xs text-[#7d8b96]">{prop.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ArrAutomationPanel({
  activePortalId,
  manualDealId,
  onManualDealIdChange,
  onManualArrPreview,
  onManualArrWrite,
  manualArrPreviewLoading,
  manualArrWriteLoading,
  manualArrPreview,
  manualArrResult,
}) {
  const [arrConfig, setArrConfig] = useState(null);
  const [draftConfig, setDraftConfig] = useState({
    trigger_property: "",
    target_property: "",
    archive_property: "",
    automation_enabled: false,
  });
  const [allProperties, setAllProperties] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configError, setConfigError] = useState("");
  const [configSaved, setConfigSaved] = useState(false);

  // Load config + deal property list whenever the active portal changes
  useEffect(() => {
    if (!activePortalId) return;
    setConfigError("");
    setConfigSaved(false);
    setArrConfig(null);
    setAllProperties([]);

    const load = async () => {
      setLoadingConfig(true);
      try {
        const [config, propDefs] = await Promise.all([
          fetchPortalArrConfig(activePortalId),
          fetchDealPropertyDefs(activePortalId),
        ]);
        setArrConfig(config);
        setDraftConfig({
          trigger_property: config.trigger_property || "",
          target_property: config.target_property || "",
          archive_property: config.archive_property || "",
          automation_enabled: config.automation_enabled ?? false,
        });
        setAllProperties(
          (propDefs.properties || []).sort((a, b) => a.label.localeCompare(b.label))
        );
      } catch {
        setConfigError("Could not load ARR configuration for this portal.");
      } finally {
        setLoadingConfig(false);
      }
    };
    load();
  }, [activePortalId]);

  const handleSave = async () => {
    if (!draftConfig.trigger_property || !draftConfig.target_property || !draftConfig.archive_property) {
      setConfigError("Please select all three properties before saving.");
      return;
    }
    setConfigError("");
    setSavingConfig(true);
    try {
      const saved = await savePortalArrConfig(activePortalId, draftConfig);
      setArrConfig(saved);
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
    } catch {
      setConfigError("Failed to save configuration. Please try again.");
    } finally {
      setSavingConfig(false);
    }
  };

  const isConfigured = arrConfig?.is_configured;
  const automationEnabled = arrConfig?.automation_enabled === true;
  const isDirty =
    draftConfig.trigger_property !== (arrConfig?.trigger_property || "") ||
    draftConfig.target_property !== (arrConfig?.target_property || "") ||
    draftConfig.archive_property !== (arrConfig?.archive_property || "") ||
    draftConfig.automation_enabled !== (arrConfig?.automation_enabled ?? false);

  // Favour typed properties in the dropdowns, fall back to all if none match
  const dateProps = allProperties.filter((p) => p.type === "date" || p.field_type === "date");
  const numberProps = allProperties.filter((p) => p.type === "number" || p.field_type === "number");
  const triggerProps = dateProps.length > 0 ? dateProps : allProperties;
  const writebackProps = numberProps.length > 0 ? numberProps : allProperties;

  return (
    <div className="grid gap-6 xl:grid-cols-2" data-testid="arr-automation-panel">

      {/* ── ARR Config Card ───────────────────────────────────────────── */}
      <Card className="border-[#dddddd] bg-white shadow-sm" data-testid="arr-config-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-[#4a5b68]" />
            <CardTitle className="font-sans text-2xl text-[#323c44]">ARR automation setup</CardTitle>
          </div>
          <CardDescription className="font-sans text-sm leading-7 text-[#4a5b68]">
            Choose which HubSpot deal properties drive the ARR calculation for this portal. Every portal can use different properties — select from whatever is available in your HubSpot account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">

          {/* Status banners */}
          {!loadingConfig && !isConfigured && (
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <p className="text-sm text-amber-800">
                ARR automation is not yet configured for this portal. Select your three properties below and save to enable automatic writeback.
              </p>
            </div>
          )}

          {!loadingConfig && isConfigured && automationEnabled && (
            <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <p className="text-sm text-emerald-800">
                Automatic writeback is <span className="font-semibold">on</span>. When <span className="font-semibold">{arrConfig.trigger_property}</span> changes on a deal, PortalIQ recalculates and writes the result to <span className="font-semibold">{arrConfig.target_property}</span>.
              </p>
            </div>
          )}
          {!loadingConfig && isConfigured && !automationEnabled && (
            <div className="flex items-start gap-3 rounded-md border border-[#dddddd] bg-[#f5f5f5] px-4 py-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#7d8b96]" />
              <p className="text-sm text-[#4a5b68]">
                Automatic writeback is <span className="font-semibold">off</span>. Properties are configured — toggle on above to enable automatic recalculation, or use the manual tool on the right.
              </p>
            </div>
          )}

          {loadingConfig && (
            <div className="rounded-md border border-[#dddddd] bg-[#f5f5f5] px-4 py-6 text-center text-sm text-[#4a5b68]">
              Loading deal properties from HubSpot…
            </div>
          )}

          {!loadingConfig && (
            <>
              {/* Trigger property */}
              <div className="space-y-1.5">
                <label className="block text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">
                  Trigger property <span className="normal-case font-normal text-[#7d8b96]">— date field</span>
                </label>
                <p className="text-xs text-[#7d8b96]">When this property changes on a deal, the ARR calculation runs automatically.</p>
                <PropertyCombobox
                  value={draftConfig.trigger_property}
                  onChange={(v) => setDraftConfig((c) => ({ ...c, trigger_property: v }))}
                  properties={triggerProps}
                  placeholder="e.g. current_year_effective_date"
                />
              </div>

              {/* Target / writeback property */}
              <div className="space-y-1.5">
                <label className="block text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">
                  ARR writeback property <span className="normal-case font-normal text-[#7d8b96]">— number field</span>
                </label>
                <p className="text-xs text-[#7d8b96]">The calculated ARR value is written to this property on the deal.</p>
                <PropertyCombobox
                  value={draftConfig.target_property}
                  onChange={(v) => setDraftConfig((c) => ({ ...c, target_property: v }))}
                  properties={writebackProps}
                  placeholder="e.g. annual_recurring_revenue"
                />
              </div>

              {/* Archive property */}
              <div className="space-y-1.5">
                <label className="block text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">
                  Archive property <span className="normal-case font-normal text-[#7d8b96]">— number field</span>
                </label>
                <p className="text-xs text-[#7d8b96]">Before writing new ARR, the previous value is stored here so you can track year-on-year changes.</p>
                <PropertyCombobox
                  value={draftConfig.archive_property}
                  onChange={(v) => setDraftConfig((c) => ({ ...c, archive_property: v }))}
                  properties={writebackProps}
                  placeholder="e.g. arr_last_year"
                />
              </div>

              {/* Automation enable/disable toggle */}
              <div className="flex items-center justify-between rounded-md border border-[#dddddd] bg-[#f5f5f5] px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-[#323c44]">Enable automatic writeback</p>
                  <p className="text-xs text-[#7d8b96] mt-0.5">
                    When on, ARR is automatically recalculated and written to HubSpot when the trigger property changes.
                    Turn off to use manual recalculation only.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={draftConfig.automation_enabled}
                  onClick={() => setDraftConfig((c) => ({ ...c, automation_enabled: !c.automation_enabled }))}
                  className={`relative ml-4 inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    draftConfig.automation_enabled ? "bg-emerald-500" : "bg-[#cccccc]"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ${
                      draftConfig.automation_enabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {configError && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{configError}</p>
              )}

              <div className="flex items-center gap-3 pt-1">
                <Button
                  onClick={handleSave}
                  disabled={savingConfig || (!isDirty && isConfigured)}
                  className="bg-[#50e3c2] text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50"
                  data-testid="arr-config-save-button"
                >
                  {savingConfig ? "Saving…" : isConfigured ? "Update configuration" : "Save & enable automation"}
                </Button>
                {configSaved && (
                  <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" /> Saved
                  </span>
                )}
              </div>

              {/* Webhook registration note */}
              {arrConfig?.webhook_url && isConfigured && (
                <div className="mt-2 rounded-md border border-blue-100 bg-blue-50 px-4 py-3">
                  <p className="text-xs text-blue-800">
                    <span className="font-semibold">Property-change trigger registered.</span> PortalIQ automatically subscribed to <span className="font-mono">{arrConfig.trigger_property}</span> changes in HubSpot — no manual setup needed.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Manual Recalculation Card ─────────────────────────────────── */}
      <Card className="border-[#dddddd] bg-white shadow-sm" data-testid="arr-manual-recalculate-card">
        <CardHeader>
          <CardTitle className="font-sans text-2xl text-[#323c44]">Manual ARR recalculation</CardTitle>
          <CardDescription className="font-sans text-sm leading-7 text-[#4a5b68]">
            Trigger the ARR writeback on a specific deal directly from the app, using this portal's configured properties.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isConfigured && !loadingConfig && (
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <p className="text-sm text-amber-800">Configure ARR automation on the left before running manual recalculations.</p>
            </div>
          )}

          <div className="rounded-md border border-[#dddddd] bg-[#f5f5f5] p-4" data-testid="manual-arr-card">
            <label className="block text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]" htmlFor="manual-arr-deal-id-input">
              HubSpot deal ID
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                id="manual-arr-deal-id-input"
                type="text"
                value={manualDealId}
                onChange={(e) => onManualDealIdChange(e.target.value)}
                placeholder="Enter a deal ID"
                disabled={!isConfigured}
                className="h-11 flex-1 rounded-md border border-[#dddddd] bg-white px-3 text-sm text-[#323c44] disabled:opacity-50"
                data-testid="manual-arr-deal-id-input"
              />
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onManualArrPreview}
                  disabled={manualArrPreviewLoading || !manualDealId || !isConfigured}
                  className="h-11 border-[#dddddd] bg-white text-[#323c44] hover:bg-[#efefef] disabled:opacity-50"
                  data-testid="manual-arr-preview-button"
                >
                  {manualArrPreviewLoading ? "Loading…" : "Preview"}
                </Button>
                <Button
                  type="button"
                  onClick={onManualArrWrite}
                  disabled={manualArrWriteLoading || !manualArrPreview || !isConfigured}
                  className="h-11 bg-[#50e3c2] text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50"
                  data-testid="manual-arr-write-button"
                >
                  {manualArrWriteLoading ? "Writing…" : "Write back"}
                </Button>
              </div>
            </div>
          </div>

          {/* Preview / result display */}
          {(manualArrPreview || manualArrResult) && (() => {
            const data = manualArrResult || manualArrPreview;
            const backfill = data.last_year_backfill;

            const LineItemsTable = ({ items }) => (
              <div className="overflow-hidden rounded-md border border-[#dddddd]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#f5f5f5]">
                      <TableHead className="text-xs font-semibold uppercase tracking-wide text-[#4a5b68]">Line item</TableHead>
                      <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-[#4a5b68]">Frequency</TableHead>
                      <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-[#4a5b68]">Monthly MRR</TableHead>
                      <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-[#4a5b68]">Months</TableHead>
                      <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-[#4a5b68]">Period value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.line_item_id}>
                        <TableCell className="text-sm text-[#323c44]">{item.line_item_name}</TableCell>
                        <TableCell className="text-right text-sm capitalize text-[#4a5b68]">{item.billing_frequency}</TableCell>
                        <TableCell className="text-right text-sm text-[#323c44]">{item.monthly_mrr}</TableCell>
                        <TableCell className="text-right text-sm text-[#4a5b68]">{item.months_included}</TableCell>
                        <TableCell className="text-right text-sm font-medium text-[#323c44]">{item.period_value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );

            return (
              <div className="space-y-4">
                {/* ── This year ── */}
                <div className="space-y-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">This year</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      ["Status", data.status],
                      ["Deal ID", data.deal_id],
                      ["Reference date", data.reference_date],
                      ["Window", `${data.window_start} → ${data.window_end}`],
                      ["Previous ARR", data.previous_arr],
                      ["New ARR", data.annual_recurring_revenue],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-md border border-[#dddddd] bg-white p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">{label}</p>
                        <p className="mt-1 font-medium text-[#323c44]">{String(value ?? "—")}</p>
                      </div>
                    ))}
                  </div>
                  {data.matched_line_items?.length > 0 && <LineItemsTable items={data.matched_line_items} />}
                </div>

                {/* ── Last year backfill (first-run only) ── */}
                {backfill && (
                  <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
                      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-700">
                        Last year backfill
                      </p>
                    </div>
                    <p className="text-xs text-amber-700">
                      Last year ARR property was empty — calculated from line items active during{" "}
                      <strong>{backfill.window_start} → {backfill.window_end}</strong> and will be
                      written to the archive property on Write back.
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {[
                        ["Window", `${backfill.window_start} → ${backfill.window_end}`],
                        ["Last year ARR", backfill.annual_recurring_revenue],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-md border border-amber-200 bg-white p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-600">{label}</p>
                          <p className="mt-1 font-medium text-[#323c44]">{String(value ?? "—")}</p>
                        </div>
                      ))}
                    </div>
                    {backfill.matched_line_items?.length > 0 && <LineItemsTable items={backfill.matched_line_items} />}
                  </div>
                )}

                <p className="text-sm text-[#4a5b68]">{data.message}</p>
              </div>
            );
          })()}
        </CardContent>
      </Card>
      {/* ── Automation Triggers Card ──────────────────────────────────── */}
      {isConfigured && (
        <TriggersCard arrConfig={arrConfig} />
      )}
    </div>
  );
}

// ── Triggers reference card ──────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="ml-2 inline-flex items-center gap-1 rounded-full border border-[#dddddd] bg-white px-2 py-0.5 text-xs text-[#4a5b68] hover:bg-[#efefef]"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function TriggersCard({ arrConfig }) {
  const [workflowOpen, setWorkflowOpen] = useState(false);

  return (
    <Card className="border-[#dddddd] bg-white shadow-sm xl:col-span-2" data-testid="arr-triggers-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#4a5b68]" />
          <CardTitle className="font-sans text-2xl text-[#323c44]">How automation works</CardTitle>
        </div>
        <CardDescription className="font-sans text-sm leading-7 text-[#4a5b68]">
          Whenever <span className="font-mono font-medium">{arrConfig?.trigger_property}</span> changes on a deal, PortalIQ recalculates ARR and writes it back to HubSpot automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* Status */}
        <div className="flex items-start gap-4 rounded-md border border-[#dddddd] bg-[#f5f5f5] p-5">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-[#323c44]">Webhook subscription</p>
              {arrConfig?.automation_enabled
                ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Active</span>
                : <span className="rounded-full bg-[#efefef] px-2 py-0.5 text-[10px] font-semibold text-[#7d8b96]">Disabled</span>
              }
            </div>
            <p className="text-xs text-[#4a5b68]">
              PortalIQ automatically registered a HubSpot webhook subscription for <span className="font-mono font-medium text-[#323c44]">{arrConfig?.trigger_property}</span> when you saved the config. No manual setup needed — when that property changes on any deal, HubSpot notifies PortalIQ and the ARR calculation runs.
            </p>
          </div>
        </div>

        {/* Workflow tip */}
        <div className="space-y-2">
          <button
            onClick={() => setWorkflowOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded border border-[#dddddd] bg-white px-4 py-3 text-sm font-medium text-[#323c44] hover:bg-[#f5f5f5]"
          >
            Want to trigger this from a HubSpot workflow?
            {workflowOpen ? <ChevronUp className="h-4 w-4 text-[#7d8b96]" /> : <ChevronDown className="h-4 w-4 text-[#7d8b96]" />}
          </button>
          {workflowOpen && (
            <div className="rounded-md border border-[#dddddd] bg-[#f5f5f5] px-5 py-4 space-y-3">
              <p className="text-xs text-[#4a5b68]">Use a standard <span className="font-semibold">Set property value</span> action — available on all HubSpot plans — to stamp the trigger date. That property change fires the webhook above.</p>
              <ol className="space-y-2 text-xs text-[#4a5b68]">
                <li className="flex gap-2"><span className="font-bold text-[#323c44] shrink-0">1.</span>HubSpot → <span className="font-semibold">Automation → Workflows</span> → create a Deal-based workflow</li>
                <li className="flex gap-2"><span className="font-bold text-[#323c44] shrink-0">2.</span>Set enrollment trigger — any condition (stage change, close date, etc.)</li>
                <li className="flex gap-2"><span className="font-bold text-[#323c44] shrink-0">3.</span>Add action → <span className="font-semibold">Set property value</span> → set <span className="font-mono font-medium text-[#323c44]">{arrConfig?.trigger_property || "your trigger property"}</span> to <span className="font-semibold">Today</span></li>
                <li className="flex gap-2"><span className="font-bold text-[#323c44] shrink-0">4.</span>Publish — PortalIQ fires on every enrolled deal</li>
              </ol>
            </div>
          )}
        </div>

      </CardContent>
    </Card>
  );
}
